/**
 * AI Suggestions Panel Component
 * 
 * Provides AI-powered contextual suggestions based on patient data,
 * clinical alerts, and workflow patterns.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Sparkles, 
  TrendingUp, 
  AlertTriangle, 
  Lightbulb, 
  ChevronRight,
  Activity,
  FileText,
  Pill,
  Calendar,
  Clock,
  Brain,
  Target,
  Shield,
  Heart
} from "lucide-react";

// Suggestion categories with icons
const SUGGESTION_ICONS = {
  clinical: Activity,
  documentation: FileText,
  medication: Pill,
  compliance: Shield,
  scheduling: Calendar,
  care_plan: Target,
  assessment: Heart
};

// Priority colors
const PRIORITY_COLORS = {
  critical: "bg-red-500/20 border-red-500/40 text-red-300",
  high: "bg-amber-500/20 border-amber-500/40 text-amber-300",
  medium: "bg-blue-500/20 border-blue-500/40 text-blue-300",
  low: "bg-slate-500/20 border-slate-500/40 text-slate-300"
};

export function AISuggestionsPanel({ 
  patientContext, 
  onActionSelect,
  maxSuggestions = 5,
  isDark = true
}) {
  const [expanded, setExpanded] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Generate AI-powered suggestions based on patient context
  const suggestions = useMemo(() => {
    if (!patientContext) return [];

    const result = [];
    const stats = patientContext.quick_stats || {};
    const alerts = patientContext.alerts || [];

    // Analyze alerts for clinical suggestions
    const hasPainAlert = alerts.some(a => 
      a.message?.toLowerCase().includes("pain") || a.type === "pain"
    );
    const hasComplianceAlert = alerts.some(a => 
      a.type === "compliance" || a.message?.toLowerCase().includes("due")
    );
    const hasClinicalAlert = alerts.some(a => 
      a.type === "clinical" && a.severity === "high"
    );

    // Pain-related suggestions
    if (hasPainAlert) {
      result.push({
        id: "pain_assessment",
        title: "Pain Assessment Recommended",
        description: "Patient's pain score has increased. Consider immediate pain assessment and intervention.",
        category: "clinical",
        priority: "critical",
        action: "pain_management",
        reasoning: "Based on elevated pain score detected in recent documentation",
        confidence: 95
      });
      result.push({
        id: "med_review",
        title: "Review PRN Medications",
        description: "Check PRN medication effectiveness and consider titration.",
        category: "medication",
        priority: "high",
        action: "mar",
        reasoning: "Pain management protocol recommends medication review",
        confidence: 88
      });
    }

    // Compliance-related suggestions
    if (hasComplianceAlert) {
      result.push({
        id: "compliance_check",
        title: "HOPE Update Due",
        description: "HOPE assessment approaching due date. Schedule completion to maintain compliance.",
        category: "compliance",
        priority: "high",
        action: "hope_assessment",
        reasoning: "CMS requires HOPE updates within regulatory timeframes",
        confidence: 100
      });
    }

    // New admission suggestions
    if (stats.days_in_program && stats.days_in_program <= 7) {
      result.push({
        id: "admission_followup",
        title: "New Admission Follow-up",
        description: "Patient is within first week. Ensure comprehensive initial assessment completed.",
        category: "documentation",
        priority: "high",
        action: "visit_note",
        reasoning: "First week requires thorough initial documentation",
        confidence: 92
      });
      result.push({
        id: "family_contact",
        title: "Family Education Recommended",
        description: "New admissions benefit from early family education and support setup.",
        category: "care_plan",
        priority: "medium",
        action: "family_message",
        reasoning: "Early family engagement improves care outcomes",
        confidence: 85
      });
    }

    // Recertification suggestions
    if (stats.benefit_period === "2nd" || stats.benefit_period === "3rd") {
      result.push({
        id: "recert_prep",
        title: "Recertification Preparation",
        description: `Patient in ${stats.benefit_period} benefit period. Review CTI documentation needs.`,
        category: "compliance",
        priority: "medium",
        action: "recertification",
        reasoning: "Multiple benefit periods require thorough recertification documentation",
        confidence: 90
      });
    }

    // Long-stay patient suggestions
    if (stats.days_in_program && stats.days_in_program > 90) {
      result.push({
        id: "care_plan_review",
        title: "Comprehensive Care Plan Review",
        description: "Extended stay patient - recommend quarterly care plan evaluation.",
        category: "care_plan",
        priority: "medium",
        action: "plan_of_care",
        reasoning: "Long-term patients benefit from regular care plan reassessment",
        confidence: 82
      });
    }

    // General clinical intelligence suggestions
    if (hasClinicalAlert) {
      result.push({
        id: "clinical_synthesis",
        title: "Run Clinical Logic Analysis",
        description: "New clinical data available. 14-day lookback analysis may identify trends.",
        category: "clinical",
        priority: "medium",
        action: "clinical_logic",
        reasoning: "AI synthesis can identify patterns in recent documentation",
        confidence: 87
      });
    }

    // Default documentation suggestion
    if (result.length === 0) {
      result.push({
        id: "routine_visit",
        title: "Routine Visit Documentation",
        description: "No urgent items. Proceed with standard visit documentation.",
        category: "documentation",
        priority: "low",
        action: "visit_note",
        reasoning: "Maintaining regular documentation supports continuity of care",
        confidence: 75
      });
    }

    // Add timestamp-based suggestion
    const currentHour = new Date().getHours();
    if (currentHour >= 17) {
      result.push({
        id: "end_of_day",
        title: "End-of-Day Documentation Check",
        description: "Review any pending documentation before end of shift.",
        category: "documentation",
        priority: "low",
        action: "smart_documentation",
        reasoning: "Complete documentation reduces carryover and improves handoffs",
        confidence: 70
      });
    }

    return result.slice(0, maxSuggestions);
  }, [patientContext, maxSuggestions]);

  // Simulate AI analysis animation - using ref to avoid cascading renders
  const prevPatientId = useRef(patientContext?.patient_id);
  
  useEffect(() => {
    // Only trigger animation when patient changes
    if (patientContext?.patient_id && patientContext.patient_id !== prevPatientId.current) {
      prevPatientId.current = patientContext.patient_id;
      // Use requestAnimationFrame to batch state updates
      requestAnimationFrame(() => {
        setIsAnalyzing(true);
        const timer = setTimeout(() => setIsAnalyzing(false), 800);
        return () => clearTimeout(timer);
      });
    }
  }, [patientContext?.patient_id]);

  if (!patientContext) return null;

  return (
    <div className="mb-6" data-testid="ai-suggestions-panel">
      <div 
        className="flex items-center justify-between mb-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h3 className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-2 ${
          isDark ? 'text-slate-500' : 'text-gray-500'
        }`}>
          <Sparkles className="w-3 h-3 text-violet-400" />
          AI Suggestions
          {isAnalyzing && (
            <span className="flex items-center gap-1 text-violet-400 normal-case font-normal">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
              Analyzing...
            </span>
          )}
        </h3>
        <Badge className={isDark ? 'bg-violet-500/20 text-violet-300 text-xs' : 'bg-violet-100 text-violet-700 text-xs'}>
          {suggestions.length} insights
        </Badge>
      </div>

      {expanded && (
        <div className="space-y-2">
          {suggestions.map((suggestion) => {
            const Icon = SUGGESTION_ICONS[suggestion.category] || Lightbulb;
            const priorityClass = isDark 
              ? PRIORITY_COLORS[suggestion.priority] || PRIORITY_COLORS.low
              : (suggestion.priority === "critical" ? "bg-red-50 border-red-200 text-red-700" :
                 suggestion.priority === "high" ? "bg-amber-50 border-amber-200 text-amber-700" :
                 suggestion.priority === "medium" ? "bg-blue-50 border-blue-200 text-blue-700" :
                 "bg-gray-50 border-gray-200 text-gray-600");

            return (
              <Card 
                key={suggestion.id}
                className={`border cursor-pointer transition-all hover:scale-[1.02] ${priorityClass}`}
                onClick={() => onActionSelect?.(suggestion.action)}
                data-testid={`suggestion-${suggestion.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      suggestion.priority === "critical" ? (isDark ? "bg-red-500/30" : "bg-red-100") :
                      suggestion.priority === "high" ? (isDark ? "bg-amber-500/30" : "bg-amber-100") :
                      isDark ? "bg-slate-700" : "bg-gray-100"
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {suggestion.title}
                        </span>
                        {suggestion.priority === "critical" && (
                          <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />
                        )}
                      </div>
                      <p className={`text-xs line-clamp-2 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                        {suggestion.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          <Brain className="w-3 h-3" />
                          <span>{suggestion.confidence}% confidence</span>
                        </div>
                        <ChevronRight className={`w-3 h-3 ml-auto ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* AI Reasoning Tooltip */}
          <div className={`mt-2 p-2 rounded-lg ${
            isDark ? 'bg-slate-800/30 border border-slate-700/50' : 'bg-gray-50 border border-gray-200'
          }`}>
            <p className={`text-xs flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              <TrendingUp className="w-3 h-3" />
              Suggestions powered by clinical pattern analysis
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default AISuggestionsPanel;
